<template>
  <div class="signature-pad-wrapper">
    <div class="border rounded" style="background: #fff">
      <canvas
        ref="canvasRef"
        :width="canvasWidth"
        height="150"
        style="
          width: 100%;
          height: 150px;
          cursor: crosshair;
          display: block;
          touch-action: none;
        "
        @mousedown="startDraw"
        @mousemove="draw"
        @mouseup="stopDraw"
        @mouseleave="stopDraw"
        @touchstart.prevent="startDrawTouch"
        @touchmove.prevent="drawTouch"
        @touchend="stopDraw"
      ></canvas>
    </div>
    <div class="mt-2 d-flex align-items-center gap-2">
      <button
        type="button"
        class="btn btn-outline-secondary btn-sm"
        @click="clear"
      >
        <i class="fa fa-eraser me-1"></i> Hapus Tanda Tangan
      </button>
      <small class="text-muted"
        >Gambar tanda tangan Anda di area di atas.</small
      >
    </div>
  </div>
</template>

<script>
export default {
  name: "SignaturePad",

  props: {
    resetTrigger: {
      type: Number,
      default: 0,
    },
    // base64 (data:image/png;base64,...) atau URL gambar tanda tangan dari API.
    // Jika diisi, gambar akan di-render ke canvas secara otomatis.
    initialImage: {
      type: String,
      default: null,
    },
  },

  emits: ["update"],

  data() {
    return {
      isDrawing: false,
      canvasWidth: 700,
      ctx: null,
    };
  },

  watch: {
    resetTrigger() {
      this.clear();
    },

    // Saat prop initialImage berubah (setelah getValidatorData selesai),
    // langsung render gambar ke canvas
    initialImage(val) {
      if (val) {
        this.$nextTick(() => this.loadImage(val));
      } else {
        this.clear();
      }
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.initCanvas();
      if (this.initialImage) {
        this.loadImage(this.initialImage);
      }
    });
  },

  methods: {
    initCanvas() {
      const canvas = this.$refs.canvasRef;
      if (!canvas) return;
      this.ctx = canvas.getContext("2d");
      this.ctx.strokeStyle = "#000000";
      this.ctx.lineWidth = 2;
      this.ctx.lineCap = "round";
      this.ctx.lineJoin = "round";
    },

    // ── Render gambar (base64 atau URL) ke canvas, proporsional di tengah ──
    loadImage(src) {
      const canvas = this.$refs.canvasRef;
      if (!canvas || !this.ctx) return;

      const img = new Image();

      // Izinkan gambar cross-origin jika src berupa URL eksternal
      img.crossOrigin = "anonymous";

      img.onload = () => {
        // Bersihkan canvas dulu
        this.ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Hitung skala agar gambar fit di dalam canvas tanpa distorsi
        const scale = Math.min(
          canvas.width / img.width,
          canvas.height / img.height,
        );
        const drawW = img.width * scale;
        const drawH = img.height * scale;
        const offsetX = (canvas.width - drawW) / 2;
        const offsetY = (canvas.height - drawH) / 2;

        this.ctx.drawImage(img, offsetX, offsetY, drawW, drawH);

        // Emit base64 hasil render agar form.tanda_tangan ikut terupdate
        this.$emit("update", this.toBase64());
      };

      img.onerror = () => {
        console.warn("SignaturePad: gagal memuat gambar tanda tangan.");
      };

      img.src = src;
    },

    getPos(e) {
      const canvas = this.$refs.canvasRef;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
    },

    startDraw(e) {
      if (!this.ctx) return;
      this.isDrawing = true;
      const pos = this.getPos(e);
      this.ctx.beginPath();
      this.ctx.moveTo(pos.x, pos.y);
    },

    draw(e) {
      if (!this.isDrawing || !this.ctx) return;
      const pos = this.getPos(e);
      this.ctx.lineTo(pos.x, pos.y);
      this.ctx.stroke();
    },

    startDrawTouch(e) {
      if (!this.ctx) return;
      const touch = e.touches[0];
      const canvas = this.$refs.canvasRef;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      this.isDrawing = true;
      this.ctx.beginPath();
      this.ctx.moveTo(
        (touch.clientX - rect.left) * scaleX,
        (touch.clientY - rect.top) * scaleY,
      );
    },

    drawTouch(e) {
      if (!this.isDrawing || !this.ctx) return;
      const touch = e.touches[0];
      const canvas = this.$refs.canvasRef;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      this.ctx.lineTo(
        (touch.clientX - rect.left) * scaleX,
        (touch.clientY - rect.top) * scaleY,
      );
      this.ctx.stroke();
    },

    stopDraw() {
      if (!this.isDrawing) return;
      this.isDrawing = false;
      this.$emit("update", this.toBase64());
    },

    clear() {
      const canvas = this.$refs.canvasRef;
      if (!canvas || !this.ctx) return;
      this.ctx.clearRect(0, 0, canvas.width, canvas.height);
      this.$emit("update", null);
    },

    toBase64() {
      const canvas = this.$refs.canvasRef;
      return canvas ? canvas.toDataURL("image/png") : null;
    },

    // Cek alpha channel — canvas kosong semua alpha = 0, ada gambar alpha > 0
    isBlank() {
      const canvas = this.$refs.canvasRef;
      if (!canvas) return true;
      const pixels = canvas
        .getContext("2d")
        .getImageData(0, 0, canvas.width, canvas.height).data;
      for (let i = 3; i < pixels.length; i += 4) {
        if (pixels[i] > 0) return false;
      }
      return true;
    },
  },
};
</script>
